/**
 * Tenant store (Zustand): holds the resolved public tenant configuration.
 *
 * The useTenant hook writes into this store on successful fetch so branding
 * and map config are available synchronously across pages/components
 * without prop drilling. The store also keeps the last-known-good config
 * around so a transient refetch failure doesn't un-brand the shell.
 */
import { create } from 'zustand';
import type { TenantConfig } from '../lib/api';

export type TenantStatus = 'idle' | 'loading' | 'ready' | 'error';

interface TenantState {
  tenant: TenantConfig | null;
  slug: string | null;
  status: TenantStatus;
  /** Human-readable error for the ErrorState screen. */
  errorMessage: string | null;

  setLoading: (slug: string) => void;
  setReady: (tenant: TenantConfig) => void;
  setError: (slug: string, message: string) => void;
  reset: () => void;
}

export const useTenantStore = create<TenantState>()((set) => ({
  tenant: null,
  slug: null,
  status: 'idle',
  errorMessage: null,

  setLoading: (slug) =>
    set((state) => ({
      status: 'loading',
      slug,
      // Keep the previous tenant while refetching; clear a stale error.
      errorMessage: null,
      tenant: state.slug === slug ? state.tenant : null,
    })),

  setReady: (tenant) =>
    set({
      tenant,
      slug: tenant.slug,
      status: 'ready',
      errorMessage: null,
    }),

  setError: (slug, errorMessage) =>
    set({
      slug,
      status: 'error',
      errorMessage,
    }),

  reset: () =>
    set({
      tenant: null,
      slug: null,
      status: 'idle',
      errorMessage: null,
    }),
}));

/** Brand helpers derived from the tenant config with safe defaults. */
export function selectBranding(tenant: TenantConfig | null): {
  primary: string;
  accent: string;
  logoUrl: string | null;
} {
  const primary = tenant?.branding.primary_color ?? '#14532D';
  return {
    primary,
    accent: tenant?.branding.accent_color ?? '#E8912D',
    logoUrl: tenant?.branding.logo_url ?? null,
  };
}
